import {
    IntegratedServiceManager,
    SERVICE_PROVIDER_MANAGED_BY_LANDLORD_ID,
    SERVICE_PROVIDER_MANAGED_BY_LANDLORD_LABEL,
    SERVICE_PROVIDER_NOT_MANAGED_ID,
    SERVICE_PROVIDER_NOT_MANAGED_LABEL,
    ServiceProviderOption,
} from '@acp/shared';
import {
    addMonths,
    format,
} from 'date-fns';
// ============================================================================
// Constants & Utilities
// ============================================================================

/** Format a Date as local calendar day YYYY-MM-DD (no UTC shift). */
export function formatLocalDateYYYYMMDD(d: Date): string {
    return format(d, 'yyyy-MM-dd');
}

// ============================================================================
// Date Helpers
// ============================================================================

/**
 * Compute suggested next service date given a last service date and frequency.
 * Returns null if inputs are invalid or frequency <= 0.
 */
export function getSuggestedNextServiceDate(
    lastServiceDate: Date | null,
    frequencyMonths: number | null | undefined
): Date | null {
    if (!(lastServiceDate instanceof Date)) return null;
    if (frequencyMonths === null || frequencyMonths === undefined) return null;
    if (typeof frequencyMonths !== 'number' || Number.isNaN(frequencyMonths)) return null;
    if (frequencyMonths <= 0) return null;
    return addMonths(lastServiceDate, frequencyMonths);
}

// ============================================================================
// Service Provider Helpers
// ============================================================================
/**
 * Hydrate the providedBy field based on managedBy and agencyPortfolioSupplierId.
 * Since we received providedBy from the DB as string | null
 * But we updating and submitting changes as number | null
 */
export function hydrateProvidedBy({
    providedBy,
    managedBy,
    supplierOptions,
}: {
    providedBy: string | null;
    managedBy: IntegratedServiceManager;
    supplierOptions: ServiceProviderOption[];
}): { id: number | null; providedBy: string | null } {
    if (managedBy === IntegratedServiceManager.Unknown) {
        return {
            id: null,
            providedBy: null,
        };
    }

    if (managedBy === IntegratedServiceManager.NotManaged) {
        return {
            id: SERVICE_PROVIDER_NOT_MANAGED_ID,
            providedBy: SERVICE_PROVIDER_NOT_MANAGED_LABEL,
        };
    }

    if (managedBy === IntegratedServiceManager.Landlord) {
        return {
            id: SERVICE_PROVIDER_MANAGED_BY_LANDLORD_ID,
            providedBy: SERVICE_PROVIDER_MANAGED_BY_LANDLORD_LABEL,
        };
    }

    const match = supplierOptions.find((opt) => opt.value.providedBy === providedBy);
    if (match) return match.value;

    return {
        id: null,
        providedBy: null,
    };
}
